import {
  definePermissions,
  NOBODY_CAN,
  type ExpressionBuilder
} from '@rocicorp/zero';
import { PUBLIC_ORGANIZATION_ID } from './ent/root-entity-ids.ts';
import { schema as generatedSchema } from './generated/zero-schema.gen.ts';

/** Edge-table join pattern used by chat (org → channel → message). */
function edgeLinkedMany(destSchema: 'chatChannels' | 'chatMessages') {
  return [
    {
      sourceField: ['id'],
      destField: ['targetId'],
      destSchema: 'edges',
      cardinality: 'many'
    },
    {
      sourceField: ['sourceId'],
      destField: ['id'],
      destSchema,
      cardinality: 'many'
    }
  ] as const;
}

/** Reverse of edgeLinkedMany: walk from a channel/message back up to its owner. */
function edgeLinkedManyReverse(destSchema: 'organizations' | 'chatChannels') {
  return [
    {
      sourceField: ['id'],
      destField: ['sourceId'],
      destSchema: 'edges',
      cardinality: 'many'
    },
    {
      sourceField: ['targetId'],
      destField: ['id'],
      destSchema,
      cardinality: 'many'
    }
  ] as const;
}

/**
 * drizzle-zero regen only emits tables from drizzle-zero.config.ts and may omit
 * edge-based relationships + legacy query API flags. Merge them here for chat UI
 * and for the org-membership joins the permission rules below rely on.
 */
const schema = {
  ...generatedSchema,
  relationships: {
    organizations: {
      chatChannels: edgeLinkedMany('chatChannels'),
      files: [
        {
          sourceField: ['id'],
          destField: ['organizationId'],
          destSchema: 'files',
          cardinality: 'many'
        }
      ],
      organizationUsers: [
        {
          sourceField: ['id'],
          destField: ['organizationId'],
          destSchema: 'organizationUsers',
          cardinality: 'many'
        }
      ],
      // Raw edge rows where this org is the source -- covers both the
      // PUBLIC org's shared item catalog and any org's own inventory links.
      // Kept as raw edges (not chained straight to inventoryItems) so
      // .related('item') and .related('quantityProperties') can both be
      // pulled off the same edge row in one query.
      inventoryItemEdges: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      // Same raw-edges-where-org-is-source shape as inventoryItemEdges
      // above, named separately for readability at its one call site
      // (recipes aren't inventory items) -- both could share one
      // relationship since neither constrains target type, but distinct
      // names read better than a generic one at the query call site.
      recipeEdges: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      // Same shape again, for menus -- unlike inventoryItems/recipes, menus
      // are NOT a public catalog (no PUBLIC_ORGANIZATION_ID overlay), so
      // this is only used to resolve an org's own menus, not a shared list.
      menuEdges: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      // Same shape again, for 86'd (unavailable) items -- per-org business
      // data like menus, not a public catalog.
      unavailableItemEdges: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      // Same shape again, for announcements -- per-org business data like
      // menus, not a public catalog.
      announcementEdges: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      // Same shape again, for manager logbook entries -- per-org business
      // data like menus, not a public catalog.
      logbookEntryEdges: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ]
    },
    chatChannels: {
      chatMessages: edgeLinkedMany('chatMessages'),
      organizations: edgeLinkedManyReverse('organizations'),
      createdBy: [
        {
          sourceField: ['createdByUserId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ],
      members: [
        {
          sourceField: ['id'],
          destField: ['channelId'],
          destSchema: 'chatChannelMembers',
          cardinality: 'many'
        }
      ],
      reads: [
        {
          sourceField: ['id'],
          destField: ['channelId'],
          destSchema: 'chatChannelReads',
          cardinality: 'many'
        }
      ],
      favorites: [
        {
          sourceField: ['id'],
          destField: ['channelId'],
          destSchema: 'chatChannelFavorites',
          cardinality: 'many'
        }
      ]
    },
    chatMessages: {
      sender: [
        {
          sourceField: ['senderId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ],
      chatChannels: edgeLinkedManyReverse('chatChannels'),
      files: [
        {
          sourceField: ['id'],
          destField: ['parentId'],
          destSchema: 'files',
          cardinality: 'many'
        }
      ]
    },
    users: {
      organizationUsers: [
        {
          sourceField: ['id'],
          destField: ['userId'],
          destSchema: 'organizationUsers',
          cardinality: 'many'
        }
      ],
      contact: [
        {
          sourceField: ['id'],
          destField: ['userId'],
          destSchema: 'userContacts',
          cardinality: 'one'
        }
      ]
    },
    userContacts: {
      user: [
        {
          sourceField: ['userId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ]
    },
    organizationUsers: {
      organizations: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      user: [
        {
          sourceField: ['userId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ]
    },
    chatChannelMembers: {
      channel: [
        {
          sourceField: ['channelId'],
          destField: ['id'],
          destSchema: 'chatChannels',
          cardinality: 'one'
        }
      ],
      user: [
        {
          sourceField: ['userId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ]
    },
    chatChannelReads: {
      channel: [
        {
          sourceField: ['channelId'],
          destField: ['id'],
          destSchema: 'chatChannels',
          cardinality: 'one'
        }
      ],
      user: [
        {
          sourceField: ['userId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ]
    },
    chatChannelFavorites: {
      channel: [
        {
          sourceField: ['channelId'],
          destField: ['id'],
          destSchema: 'chatChannels',
          cardinality: 'one'
        }
      ],
      user: [
        {
          sourceField: ['userId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ]
    },
    edges: {
      // Resolves an org->item edge's target as the inventory item itself.
      item: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'inventoryItems',
          cardinality: 'one'
        }
      ],
      // Quantity (and any other) properties attached to THIS edge, e.g. the
      // org->item edge -> a property-edge -> a property row. Junction
      // (2-hop), so filter/sort client-side (see zero-schema-relationships
      // note on orderBy elsewhere in this file).
      quantityProperties: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'edges',
          cardinality: 'many'
        },
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'properties',
          cardinality: 'many'
        }
      ],
      // The four relationships below exist ONLY to let the permission rules
      // further down this file authorize `edges` rows by walking back to an
      // organization the caller belongs to -- `edges` is a generic
      // polymorphic table (source_type/target_type/label aren't synced), so
      // a declarative rule can't switch on "what kind of edge is this" the
      // way application code can. Each relationship simply finds no match
      // (and its exists() check is false) when sourceId/targetId isn't
      // actually that kind of row -- which is what lets one rule per
      // relationship safely cover every edge shape sharing that field's
      // meaning (e.g. sourceOrganization covers org->item, org->channel,
      // org->property, org->category, ... edges alike) without needing to
      // enumerate every (source_type, target_type) pair by hand.
      sourceOrganization: [
        {
          sourceField: ['sourceId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      targetOrganization: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      // Self-referential: for a 2nd-order edge whose sourceId is itself
      // another edge's id (e.g. the org->item edge -> property-edge
      // pattern), this resolves that parent edge so its own
      // sourceOrganization/targetOrganization can be checked in turn.
      parentEdge: [
        {
          sourceField: ['sourceId'],
          destField: ['id'],
          destSchema: 'edges',
          cardinality: 'one'
        }
      ],
      targetChannel: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'chatChannels',
          cardinality: 'one'
        }
      ],
      // The item->category edge's source. inventoryItems is an intentional
      // public catalog (see inventoryItems' own select rule below), so this
      // only needs an "authenticated" check, not org membership -- but it
      // must be its own narrow relationship-gated rule, not a blanket
      // isAuthenticated fallback on the whole table, or every other edge
      // shape's tightening above would be moot (OR-ing in an
      // unconditionally-true clause defeats the other rules entirely).
      sourceItem: [
        {
          sourceField: ['sourceId'],
          destField: ['id'],
          destSchema: 'inventoryItems',
          cardinality: 'one'
        }
      ],
      // Resolves an org->recipe edge's target as the recipe itself.
      recipe: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'recipes',
          cardinality: 'one'
        }
      ],
      // Resolves an org->menu edge's target as the menu itself.
      menu: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'menus',
          cardinality: 'one'
        }
      ],
      // Resolves an org->unavailable_item edge's target as the item itself.
      unavailableItem: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'unavailableItems',
          cardinality: 'one'
        }
      ],
      // Resolves an org->announcement edge's target as the announcement
      // itself.
      announcement: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'announcements',
          cardinality: 'one'
        }
      ],
      // Resolves an org->manager_logbook_entry edge's target as the entry
      // itself.
      logbookEntry: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'managerLogbookEntries',
          cardinality: 'one'
        }
      ]
    },
    inventoryItems: {
      // An item's own outgoing edges -- today only ever the item->category
      // edge (source=item, target=category); categories aren't synced to
      // Zero, so callers use the edge's targetId to look up the category in
      // the already-fetched categoryTree prop rather than relating further.
      categoryEdges: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      viaOrgEdge: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ]
    },
    properties: {
      // The edge(s) that target this property row -- for the quantity
      // pattern, the org->item edge -> property-edge -> this property. Used
      // only by the permission rules below to walk back to an organization.
      viaPropertyEdge: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ]
    },
    recipes: {
      // recipe_ingredients is its own dedicated junction table (source_id/
      // target_id + quantity columns live on it directly), unlike
      // inventory's generic edges+properties pair -- a single hop suffices.
      ingredientLinks: [
        {
          sourceField: ['id'],
          destField: ['sourceId'],
          destSchema: 'recipeIngredients',
          cardinality: 'many'
        }
      ],
      viaOrgEdge: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      files: [
        {
          sourceField: ['id'],
          destField: ['parentId'],
          destSchema: 'files',
          cardinality: 'many'
        }
      ],
      // Menus this recipe is placed on (LQD-6).
      menuItems: [
        {
          sourceField: ['id'],
          destField: ['recipeId'],
          destSchema: 'menuItems',
          cardinality: 'many'
        }
      ]
    },
    recipeIngredients: {
      ingredient: [
        {
          sourceField: ['targetId'],
          destField: ['id'],
          destSchema: 'ingredients',
          cardinality: 'one'
        }
      ],
      recipe: [
        {
          sourceField: ['sourceId'],
          destField: ['id'],
          destSchema: 'recipes',
          cardinality: 'one'
        }
      ]
    },
    ingredients: {
      preparationRecipe: [
        {
          sourceField: ['recipeId'],
          destField: ['id'],
          destSchema: 'recipes',
          cardinality: 'one'
        }
      ],
      viaOrgEdge: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      recipeLinks: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'recipeIngredients',
          cardinality: 'many'
        }
      ],
      files: [
        {
          sourceField: ['id'],
          destField: ['parentId'],
          destSchema: 'files',
          cardinality: 'many'
        }
      ]
    },
    menus: {
      // The edge(s) targeting this menu -- used only by the permission
      // rule below to walk back to the owning organization. Unlike
      // inventoryItems/recipes/properties' viaXEdge relationships, this is
      // one hop (org->menu edge directly), not two.
      viaOrgEdge: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      files: [
        {
          sourceField: ['id'],
          destField: ['parentId'],
          destSchema: 'files',
          cardinality: 'many'
        }
      ],
      // LQD-6: printed sections and placed items, plus the explicit cover.
      sections: [
        {
          sourceField: ['id'],
          destField: ['menuId'],
          destSchema: 'menuSections',
          cardinality: 'many'
        }
      ],
      items: [
        {
          sourceField: ['id'],
          destField: ['menuId'],
          destSchema: 'menuItems',
          cardinality: 'many'
        }
      ],
      coverFile: [
        {
          sourceField: ['coverFileId'],
          destField: ['id'],
          destSchema: 'files',
          cardinality: 'one'
        }
      ]
    },
    // Direct organization_id column (same shape as menuImports below) plus
    // the parent menu, whose own visibility the select rules re-check.
    menuSections: {
      organization: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      menu: [
        {
          sourceField: ['menuId'],
          destField: ['id'],
          destSchema: 'menus',
          cardinality: 'one'
        }
      ],
      items: [
        {
          sourceField: ['id'],
          destField: ['sectionId'],
          destSchema: 'menuItems',
          cardinality: 'many'
        }
      ]
    },
    menuItems: {
      organization: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      menu: [
        {
          sourceField: ['menuId'],
          destField: ['id'],
          destSchema: 'menus',
          cardinality: 'one'
        }
      ],
      section: [
        {
          sourceField: ['sectionId'],
          destField: ['id'],
          destSchema: 'menuSections',
          cardinality: 'one'
        }
      ],
      recipe: [
        {
          sourceField: ['recipeId'],
          destField: ['id'],
          destSchema: 'recipes',
          cardinality: 'one'
        }
      ]
    },
    // Allergy matrices carry organization_id directly (no org edge).
    allergyMatrices: {
      organization: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      rows: [
        {
          sourceField: ['id'],
          destField: ['matrixId'],
          destSchema: 'allergyMatrixRows',
          cardinality: 'many'
        }
      ],
      updatedBy: [
        {
          sourceField: ['updatedByUserId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ]
    },
    allergyMatrixRows: {
      organization: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      // Correlated on organizationId too, so a row is only ever read
      // through a matrix of its own organization.
      matrix: [
        {
          sourceField: ['matrixId', 'organizationId'],
          destField: ['id', 'organizationId'],
          destSchema: 'allergyMatrices',
          cardinality: 'one'
        }
      ],
      recipe: [
        {
          sourceField: ['recipeId'],
          destField: ['id'],
          destSchema: 'recipes',
          cardinality: 'one'
        }
      ]
    },
    files: {
      organization: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      // Articles carry organization_id, so the parent must also be in the
      // file's organization (there is no org edge to walk).
      knowledgeArticle: [
        {
          sourceField: ['parentId', 'organizationId'],
          destField: ['id', 'organizationId'],
          destSchema: 'knowledgeArticles',
          cardinality: 'one'
        }
      ],
      parentOrganizationEdge: [
        {
          sourceField: ['organizationId', 'parentId'],
          destField: ['sourceId', 'targetId'],
          destSchema: 'edges',
          cardinality: 'one'
        }
      ],
      chatMessage: [
        {
          sourceField: ['parentId'],
          destField: ['id'],
          destSchema: 'chatMessages',
          cardinality: 'one'
        }
      ],
      menu: [
        {
          sourceField: ['parentId'],
          destField: ['id'],
          destSchema: 'menus',
          cardinality: 'one'
        }
      ],
      recipe: [
        {
          sourceField: ['parentId'],
          destField: ['id'],
          destSchema: 'recipes',
          cardinality: 'one'
        }
      ],
      ingredient: [
        {
          sourceField: ['parentId'],
          destField: ['id'],
          destSchema: 'ingredients',
          cardinality: 'one'
        }
      ],
      menuImport: [
        {
          sourceField: ['parentId'],
          destField: ['id'],
          destSchema: 'menuImports',
          cardinality: 'one'
        }
      ]
    },
    menuImports: {
      organization: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      menu: [
        {
          sourceField: ['menuId'],
          destField: ['id'],
          destSchema: 'menus',
          cardinality: 'one'
        }
      ]
    },
    unavailableItems: {
      // Same one-hop org->item edge shape as menus.viaOrgEdge above.
      viaOrgEdge: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ],
      // "Added by" / "Restored by" on the 86 list and its analytics.
      createdBy: [
        {
          sourceField: ['createdByUserId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ],
      deletedBy: [
        {
          sourceField: ['deletedByUserId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ],
      resolvedBy: [
        {
          sourceField: ['resolvedByUserId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ]
    },
    announcements: {
      // Same one-hop org->announcement edge shape as menus.viaOrgEdge above.
      viaOrgEdge: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ]
    },
    managerLogbookEntries: {
      // Same one-hop org->entry edge shape as menus.viaOrgEdge above.
      viaOrgEdge: [
        {
          sourceField: ['id'],
          destField: ['targetId'],
          destSchema: 'edges',
          cardinality: 'many'
        }
      ]
    },
    // Direct organization_id/user_id columns (not edge-linked), same shape
    // as files.organization above.
    knowledgeArticles: {
      organization: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      author: [
        {
          sourceField: ['authorId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ],
      // LQD-16: PDF attachments on Reference articles.
      files: [
        {
          sourceField: ['id'],
          destField: ['parentId'],
          destSchema: 'files',
          cardinality: 'many'
        }
      ]
    },
    organizationSearchPreferences: {
      organization: [
        {
          sourceField: ['organizationId'],
          destField: ['id'],
          destSchema: 'organizations',
          cardinality: 'one'
        }
      ],
      user: [
        {
          sourceField: ['userId'],
          destField: ['id'],
          destSchema: 'users',
          cardinality: 'one'
        }
      ]
    }
  },
  enableLegacyQueries: true,
  // Public legacy CRUD (`z.mutate.<table>.insert`) is closed. Custom
  // mutators still use `tx.mutate.*` internally for optimistic writes.
  enableLegacyMutators: false
} as const;

/**
 * zero-cache verifies Clerk JWTs when ZERO_AUTH_JWKS_URL is set (required;
 * see zero-rollout.md). `authData.sub` is the Clerk user id. Membership
 * checks join organization_users.user_id -> users.id -> users.clerk_id so
 * invitees whose internal user id differs from Clerk `sub` still match.
 */
const permissions = definePermissions<{ sub: string }, typeof schema>(
  schema,
  () => {
    const callerMembership = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ouq: any
    ) =>
      ouq.whereExists('user', (uq: any) =>
        uq.where('clerkId', '=', authData.sub)
      );

    const isOrgMember = (
      authData: { sub: string },
      eb: ExpressionBuilder<'organizations', typeof schema>
    ) =>
      eb.exists('organizationUsers', (q: any) => callerMembership(authData, q));

    // Nested whereExists() callbacks below are typed `any` -- Zero's
    // relationship-inference types don't propagate the table literal through
    // more than one level of nested closure (a known ergonomics limitation,
    // not specific to these rules). The outer eb.exists()/whereExists()
    // relationship-name arguments are still checked against the real schema.
    const isChannelOrgMember = (
      authData: { sub: string },
      eb: ExpressionBuilder<'chatChannels', typeof schema>
    ) =>
      eb.or(
        eb.exists('members', (mq: any) =>
          mq.whereExists('user', (uq: any) =>
            uq.where('clerkId', '=', authData.sub)
          )
        ),
        eb.and(
          eb.cmp('dmChannel', '=', false),
          eb.cmp('isPrivate', '=', false),
          eb.exists('organizations', (oq: any) =>
            oq.whereExists('organizationUsers', (ouq: any) =>
              callerMembership(authData, ouq)
            )
          )
        )
      );

    const isMessageOrgMember = (
      authData: { sub: string },
      eb: ExpressionBuilder<'chatMessages', typeof schema>
    ) =>
      eb.exists('chatChannels', (cq: any) =>
        cq.where((channelEb: any) => isChannelOrgMember(authData, channelEb))
      );

    const isSelfOrSharedOrgUser = (
      authData: { sub: string },
      eb: ExpressionBuilder<'users', typeof schema>
    ) =>
      eb.or(
        eb.cmp('clerkId', '=', authData.sub),
        eb.exists('organizationUsers', (ouq: any) =>
          ouq.whereExists('organizations', (oq: any) =>
            oq.whereExists('organizationUsers', (ou2q: any) =>
              callerMembership(authData, ou2q)
            )
          )
        )
      );

    // Owner always. Org peers only when the owner opted in. Email, phone,
    // and social links live on this table so they are not columns of the
    // shared `users` row.
    const isVisibleUserContact = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.or(
        eb.exists('user', (uq: any) => uq.where('clerkId', '=', authData.sub)),
        eb.and(
          eb.exists('user', (uq: any) =>
            uq.where('shareContactWithOrg', '=', true)
          ),
          eb.exists('user', (uq: any) =>
            uq.where((userEb: any) => isSelfOrSharedOrgUser(authData, userEb))
          )
        )
      );

    const isVisibleChannelMember = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('channel', (cq: any) =>
        cq.where((channelEb: any) => isChannelOrgMember(authData, channelEb))
      );

    const isOwnChannelRead = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) => eb.exists('user', (uq: any) => uq.where('clerkId', '=', authData.sub));

    // Own row only: `organizationSearchPreferences` is a private per-user
    // default search, not shared with other organization members.
    const isOwnPreferenceRow = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) => eb.exists('user', (uq: any) => uq.where('clerkId', '=', authData.sub));

    // Kept in sync with CAPABILITIES.ManageReference in
    // src/utils/capabilities.ts's ROLE_CAPABILITIES: every role except hr,
    // employee_lead, and employee currently holds this capability.
    const MANAGE_REFERENCE_ROLES = ['admin', 'owner', 'gm', 'mgr'] as const;

    const hasManageReference = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('organization', (oq: any) =>
        oq.whereExists('organizationUsers', (ouq: any) =>
          ouq
            .where('role', 'IN', MANAGE_REFERENCE_ROLES)
            .whereExists('user', (uq: any) =>
              uq.where('clerkId', '=', authData.sub)
            )
        )
      );

    // Published articles: any current organization member. Draft/archived:
    // ManageReference holders only, mirroring EntKnowledgeArticle's privacy.
    const isVisibleKnowledgeArticleRow = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.or(
        eb.and(
          eb.cmp('status', '=', 'published'),
          eb.exists('organization', (oq: any) =>
            oq.whereExists('organizationUsers', (ouq: any) =>
              callerMembership(authData, ouq)
            )
          )
        ),
        hasManageReference(authData, eb)
      );

    const isOwnOrSharedOrgUserRow = (
      authData: { sub: string },
      eb: ExpressionBuilder<'organizationUsers', typeof schema>
    ) =>
      eb.or(
        eb.exists('user', (uq: any) => uq.where('clerkId', '=', authData.sub)),
        eb.exists('organizations', (oq: any) =>
          oq.whereExists('organizationUsers', (ouq: any) =>
            callerMembership(authData, ouq)
          )
        )
      );

    // wares has no relationship back to organizationUsers (it's shared
    // reference data, not org-scoped), so the strongest row-independent
    // check available here is "the caller sent an authenticated sub claim"
    // -- see the auth-gap note above this block. (edges used to share this
    // fallback too; see isSourceOrgEdgeVisible et al. below for why it no
    // longer needs to.)
    const isAuthenticated = <
      TTable extends keyof typeof schema.tables & string
    >(
      authData: { sub: string },
      eb: ExpressionBuilder<TTable, typeof schema>
    ) => eb.cmpLit(authData.sub, 'IS NOT', null);

    // isAuthenticated-only was the Phase 0 fallback for edges/properties
    // (this table's generic, polymorphic, and has no relationship back to
    // organizationUsers of its own) -- meaning any authenticated user from
    // any org could read any other org's edges/properties rows. The
    // relationships added above let real per-org checks run instead by
    // walking back from an edges row to whichever end of it is an
    // organization. See isSourceOrgEdgeVisible et al. below.
    //
    // NOTE on the two nested-callback shapes below: `.exists()`/
    // `.whereExists()` callbacks receive a plain Query (only `.where()` and
    // `.whereExists()` -- no `.or()`/`.cmp()`/`.exists()` directly). To
    // express an OR inside one, wrap it in `.where(eb2 => eb2.or(...))`,
    // which hands back a fresh ExpressionBuilder scoped to that query.
    // isVisibleOrgRow takes the ORGANIZATIONS-scoped Query and returns a
    // Query (via `.where()`), matching what `.exists()`/`.whereExists()`
    // callbacks are expected to return.
    const isVisibleOrgRow = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oq: any
    ) =>
      oq.where((eb2: any) =>
        eb2.or(
          eb2.cmp('id', '=', PUBLIC_ORGANIZATION_ID),
          eb2.exists('organizationUsers', (ouq: any) =>
            callerMembership(authData, ouq)
          )
        )
      );

    // Strict version of isVisibleOrgRow with no PUBLIC_ORGANIZATION_ID
    // exemption -- for tables like menus that are genuinely per-org
    // business data, not a shared public catalog. (Harmless either way in
    // practice, since nothing seeds menus under the PUBLIC org, but using
    // the catalog-exemption helper here would be misleading to a future
    // reader.)
    const isOwnOrgRow = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      oq: any
    ) =>
      oq.whereExists('organizationUsers', (ouq: any) =>
        callerMembership(authData, ouq)
      );

    // Covers every edge whose SOURCE is an organization -- org->item,
    // org->channel, org->property, org->category, ... --
    // regardless of target type, since the join simply finds no match (and
    // this exists() is false) for edges whose sourceId isn't an org id.
    const isSourceOrgEdgeVisible = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('sourceOrganization', (oq: any) =>
        isVisibleOrgRow(authData, oq)
      );

    // Covers every edge whose TARGET is an organization -- e.g. the
    // channel->org and user->org edges.
    const isTargetOrgEdgeVisible = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('targetOrganization', (oq: any) =>
        isVisibleOrgRow(authData, oq)
      );

    // Covers 2nd-order edges whose source is itself an edge id (the
    // org->item edge -> property-edge pattern) by checking the PARENT
    // edge's own source-is-an-org visibility. `peq` is a Query scoped to
    // `edges`, so this walks one more hop via `.whereExists()` rather than
    // reusing isSourceOrgEdgeVisible (which needs an ExpressionBuilder).
    const isParentEdgeVisible = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('parentEdge', (peq: any) =>
        peq.whereExists('sourceOrganization', (oq: any) =>
          isVisibleOrgRow(authData, oq)
        )
      );

    // Covers the message->channel edge specifically (source=message,
    // target=channel): visible if the caller belongs to the channel's org.
    const isChannelTargetedEdgeVisible = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('targetChannel', (cq: any) =>
        cq.where((channelEb: any) => isChannelOrgMember(authData, channelEb))
      );

    const isOwnedByVisibleOrg = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('viaOrgEdge', (peq: any) =>
        peq.whereExists('sourceOrganization', (oq: any) =>
          isVisibleOrgRow(authData, oq)
        )
      );

    // Covers the item->category edge (source=inventory item): visible only
    // when the source item itself is visible (public catalog or caller org).
    const isSourceItemEdgeVisible = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('sourceItem', (iq: any) =>
        iq.whereExists('viaOrgEdge', (peq: any) =>
          peq.whereExists('sourceOrganization', (oq: any) =>
            isVisibleOrgRow(authData, oq)
          )
        )
      );

    const isVisibleMenuChildRow = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.and(
        eb.exists('organization', (oq: any) =>
          oq.whereExists('organizationUsers', (ouq: any) =>
            callerMembership(authData, ouq)
          )
        ),
        eb.exists('menu', (mq: any) =>
          mq.whereExists('viaOrgEdge', (peq: any) =>
            peq.whereExists('sourceOrganization', (oq: any) =>
              isOwnOrgRow(authData, oq)
            )
          )
        )
      );

    // Kept in sync with CAPABILITIES.ManageMenus in src/utils/capabilities.ts's
    // ROLE_CAPABILITIES (role-lists.test.ts asserts it).
    const MANAGE_MENUS_ROLES = ['admin', 'owner', 'gm', 'mgr'] as const;

    // Active and archived matrices: any organization member (the archive is
    // visible to the whole team). Drafts: ManageMenus holders only,
    // mirroring EntAllergyMatrix's privacy.
    const isVisibleAllergyMatrix = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.or(
        eb.and(
          eb.cmp('status', 'IN', ['active', 'archived']),
          eb.exists('organization', (oq: any) =>
            oq.whereExists('organizationUsers', (ouq: any) =>
              callerMembership(authData, ouq)
            )
          )
        ),
        eb.exists('organization', (oq: any) =>
          oq.whereExists('organizationUsers', (ouq: any) =>
            ouq
              .where('role', 'IN', MANAGE_MENUS_ROLES)
              .whereExists('user', (uq: any) =>
                uq.where('clerkId', '=', authData.sub)
              )
          )
        )
      );

    // A row is visible when its matrix (in the row's organization) is.
    const isVisibleAllergyMatrixRow = (
      authData: { sub: string },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      eb: any
    ) =>
      eb.exists('matrix', (mq: any) =>
        mq.where((matrixEb: any) => isVisibleAllergyMatrix(authData, matrixEb))
      );

    return {
      organizations: {
        row: {
          select: [isOrgMember],
          // Org creation/mutation stays on the ent-only path indefinitely.
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      users: {
        row: {
          select: [isSelfOrSharedOrgUser],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      userContacts: {
        row: {
          select: [isVisibleUserContact],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      organizationUsers: {
        row: {
          select: [isOwnOrSharedOrgUserRow],
          // Membership/role changes must go through ent, not the Zero client.
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      permissions: {
        row: {
          // Not directly client-readable; synced only so custom-query
          // authorization (Phase 2) can exists()-join against it.
          select: NOBODY_CAN,
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      wares: {
        row: {
          select: [isAuthenticated],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      inventoryItems: {
        row: {
          select: [isOwnedByVisibleOrg],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // Generic key/value entities (quantity, etc.) attached to edges.
      // Scoped via the edge(s) that target this property, back to an
      // organization the caller belongs to. No isAuthenticated fallback --
      // a property with no property-edge pointing at it (none exist in
      // practice today; every live-read property is a quantity value
      // reached this way) is simply not readable, which is the intended,
      // tighter behavior.
      properties: {
        row: {
          select: [
            (authData: { sub: string }, eb: any) =>
              eb.exists('viaPropertyEdge', (peq: any) =>
                peq.whereExists('parentEdge', (ppeq: any) =>
                  ppeq.whereExists('sourceOrganization', (oq: any) =>
                    isVisibleOrgRow(authData, oq)
                  )
                )
              )
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      recipes: {
        row: {
          select: [isOwnedByVisibleOrg],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      recipeIngredients: {
        row: {
          select: [
            (authData: { sub: string }, eb: any) =>
              eb.exists('recipe', (rq: any) =>
                rq.whereExists('viaOrgEdge', (peq: any) =>
                  peq.whereExists('sourceOrganization', (oq: any) =>
                    isVisibleOrgRow(authData, oq)
                  )
                )
              )
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      ingredients: {
        row: {
          select: [
            (authData: { sub: string }, eb: any) =>
              eb.or(
                isOwnedByVisibleOrg(authData, eb),
                eb.exists('recipeLinks', (riq: any) =>
                  riq.whereExists('recipe', (rq: any) =>
                    rq.whereExists('viaOrgEdge', (peq: any) =>
                      peq.whereExists('sourceOrganization', (oq: any) =>
                        isVisibleOrgRow(authData, oq)
                      )
                    )
                  )
                )
              )
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // Unlike inventoryItems/recipes, menus are genuinely per-org business
      // data (a bar's own drink menu), not a shared public catalog -- so
      // this scopes reads to org membership via the org->menu edge, the
      // same tightening Phase 2 did for edges/properties, rather than
      // falling back to isAuthenticated.
      menus: {
        row: {
          select: [
            (authData: { sub: string }, eb: any) =>
              eb.exists('viaOrgEdge', (peq: any) =>
                peq.whereExists('sourceOrganization', (oq: any) =>
                  isOwnOrgRow(authData, oq)
                )
              )
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // 86'd items: per-org business data like menus, same one-hop
      // org->item edge shape and same tightening (no isAuthenticated
      // fallback, no PUBLIC_ORGANIZATION_ID exemption).
      unavailableItems: {
        row: {
          select: [
            (authData: { sub: string }, eb: any) =>
              eb.or(
                eb.and(
                  eb.cmp('resolvedAt', 'IS', null),
                  eb.cmp('deletedAt', 'IS', null),
                  eb.exists('viaOrgEdge', (eq: any) =>
                    eq.whereExists('sourceOrganization', (oq: any) =>
                      isOwnOrgRow(authData, oq)
                    )
                  )
                ),
                eb.exists('viaOrgEdge', (eq: any) =>
                  eq.whereExists('sourceOrganization', (oq: any) =>
                    oq.whereExists('organizationUsers', (uq: any) =>
                      uq
                        .where('role', 'admin')
                        .whereExists('user', (user: any) =>
                          user.where('clerkId', authData.sub)
                        )
                    )
                  )
                )
              )
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // Announcements: per-org business data like menus, same one-hop
      // org->announcement edge shape and same tightening.
      announcements: {
        row: {
          select: [
            (authData: { sub: string }, eb: any) =>
              eb.exists('viaOrgEdge', (peq: any) =>
                peq.whereExists('sourceOrganization', (oq: any) =>
                  isOwnOrgRow(authData, oq)
                )
              )
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // Manager logbook entries: per-org business data like menus, same
      // one-hop org->entry edge shape and same tightening.
      managerLogbookEntries: {
        row: {
          // Logbook is server-only: draft and per-revision audience checks
          // cannot be represented by the old organization-wide permission.
          select: NOBODY_CAN,
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // edges is a generic polymorphic join table (source_type/target_type/
      // label aren't synced -- see the relationship comments above), used
      // for org->{item,channel,property,category,recipe,menu,
      // ...}, channel->org, message->channel, and org->{ware,ingredient,...}
      // edges alike. The five rules below cover every shape actually read by
      // this app's live Zero UI (chat, inventory, recipes, menus) with a
      // real per-org
      // (or, for the catalog-style item->category edge, per-authentication)
      // check. Edges sourced from tables that aren't org-scoped by design
      // and aren't covered here (ware->category), or that aren't yet
      // Zero-synced at all (ingredient/source/image edges), are not
      // selectable at all -- a documented residual gap (see data-plan.md's
      // Phase 2 notes), not a regression: nothing reads them today, and they
      // only ever leaked ID linkage/counts, not synced row content.
      edges: {
        row: {
          select: [
            isSourceOrgEdgeVisible,
            isTargetOrgEdgeVisible,
            isParentEdgeVisible,
            isChannelTargetedEdgeVisible,
            isSourceItemEdgeVisible
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      chatChannels: {
        row: {
          select: [isChannelOrgMember],
          // Channel rows carry no org reference of their own at insert time
          // (the org link is a separate edges-table insert right after), so
          // insert can only check "authenticated", not "member of this org".
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      chatMessages: {
        row: {
          select: [isMessageOrgMember],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      files: {
        row: {
          select: [
            (authData: { sub: string }, eb: any) =>
              eb.and(
                eb.exists('organization', (oq: any) =>
                  oq.whereExists('organizationUsers', (ouq: any) =>
                    callerMembership(authData, ouq)
                  )
                ),
                eb.or(
                  eb.and(
                    eb.cmp('parentType', '=', 'chat_messages'),
                    eb.exists('chatMessage', (mq: any) =>
                      mq.whereExists('chatChannels', (cq: any) =>
                        cq.where((channelEb: any) =>
                          isChannelOrgMember(authData, channelEb)
                        )
                      )
                    )
                  ),
                  eb.and(
                    eb.cmp('parentType', '=', 'menus'),
                    eb.exists('parentOrganizationEdge', (eq: any) => eq),
                    eb.exists('menu', (mq: any) => mq)
                  ),
                  eb.and(
                    eb.cmp('parentType', '=', 'recipes'),
                    eb.exists('parentOrganizationEdge', (eq: any) => eq),
                    eb.exists('recipe', (rq: any) => rq)
                  ),
                  eb.and(
                    eb.cmp('parentType', '=', 'menu_imports'),
                    eb.exists('parentOrganizationEdge', (eq: any) => eq),
                    eb.exists('menuImport', (iq: any) => iq)
                  ),
                  eb.and(
                    eb.cmp('parentType', '=', 'ingredients'),
                    eb.exists('parentOrganizationEdge', (eq: any) => eq),
                    eb.exists('ingredient', (iq: any) => iq)
                  ),
                  eb.and(
                    eb.cmp('parentType', '=', 'knowledge_articles'),
                    eb.exists('knowledgeArticle', (kq: any) =>
                      kq.where((keb: any) =>
                        isVisibleKnowledgeArticleRow(authData, keb)
                      )
                    )
                  )
                )
              )
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // Menu sections/items (LQD-6): the menuImports shape (active
      // membership in the row's organization) plus the parent menu's own
      // rule (org->menu edge to an org the caller belongs to). Mirrors
      // EntMenuSection/EntMenuItem privacy (menu-child-predicates.ts) and
      // query-authz.ts's isVisibleMenuChildRow. Writes go through the
      // server mutators only.
      menuSections: {
        row: {
          select: [isVisibleMenuChildRow],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      menuItems: {
        row: {
          select: [isVisibleMenuChildRow],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // Menu imports are per-org business data, visible to any org member
      // (same scoping as files' organization check above); writes go
      // through the server route only.
      menuImports: {
        row: {
          select: [
            (authData: { sub: string }, eb: any) =>
              eb.exists('organization', (oq: any) =>
                oq.whereExists('organizationUsers', (ouq: any) =>
                  callerMembership(authData, ouq)
                )
              )
          ],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      chatChannelMembers: {
        row: {
          select: [isVisibleChannelMember],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      chatChannelReads: {
        row: {
          select: [isOwnChannelRead],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      // Own row only: a favorite is a private per-user pin.
      chatChannelFavorites: {
        row: {
          select: [isOwnChannelRead],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      organizationSearchPreferences: {
        row: {
          select: [isOwnPreferenceRow],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      knowledgeArticles: {
        row: {
          select: [isVisibleKnowledgeArticleRow],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      allergyMatrices: {
        row: {
          select: [isVisibleAllergyMatrix],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      },
      allergyMatrixRows: {
        row: {
          select: [isVisibleAllergyMatrixRow],
          insert: NOBODY_CAN,
          update: { preMutation: NOBODY_CAN, postMutation: NOBODY_CAN },
          delete: NOBODY_CAN
        }
      }
    };
  }
);

export { permissions, schema };
export type Schema = typeof schema;

declare module '@rocicorp/zero' {
  interface DefaultTypes {
    schema: Schema;
  }
}
